import React from 'react';
import PropTypes from 'prop-types';
import styles from './EmptyState.module.css';
import Button from './Button';

/**
 * Standardized EmptyState component for showing when no data is available
 *
 * Displays an icon, title, description, and optional action button
 *
 * Examples:
 *
 * Basic empty state:
 * <EmptyState
 *   icon="fas fa-users"
 *   title="No users found"
 *   description="Get started by creating your first user"
 * />
 *
 * With action button:
 * <EmptyState
 *   icon="fas fa-file-alt"
 *   title="No documents yet"
 *   description="Upload your first document to get started"
 *   actionLabel="Upload Document"
 *   onAction={() => handleUpload()}
 * />
 *
 * Custom size:
 * <EmptyState
 *   icon="fas fa-search"
 *   title="No results"
 *   description="Try adjusting your search criteria"
 *   size="sm"
 * />
 */
const EmptyState = ({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  actionVariant = 'primary',
  size = 'medium',
  className = '',
  children,
  ...rest
}) => {
  const classNames = [
    styles.emptyState,
    styles[`size-${size}`],
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={classNames} {...rest}>
      {icon && (
        <div className={styles.iconContainer}>
          {typeof icon === 'string' ? <i className={icon}></i> : icon}
        </div>
      )}

      {title && <h3 className={styles.title}>{title}</h3>}

      {description && <p className={styles.description}>{description}</p>}

      {children && <div className={styles.content}>{children}</div>}

      {actionLabel && onAction && (
        <div className={styles.action}>
          <Button variant={actionVariant} onClick={onAction}>
            {actionLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

EmptyState.propTypes = {
  /** Font Awesome icon class (e.g., "fas fa-users") or React Node */
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /** Main heading */
  title: PropTypes.string,

  /** Supporting description text */
  description: PropTypes.string,

  /** Label for action button */
  actionLabel: PropTypes.string,

  /** Click handler for action button */
  onAction: PropTypes.func,

  /** Button variant */
  actionVariant: PropTypes.oneOf(['primary', 'secondary', 'outline']),

  /** Size of the empty state */
  size: PropTypes.oneOf(['small', 'medium', 'large']),

  /** Additional CSS class names */
  className: PropTypes.string,

  /** Custom content (overrides action button) */
  children: PropTypes.node,
};

export default EmptyState;
